import { IntegrationProvider } from "@repo/app-types/entities/integration";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router";
import { appPaths } from "../../../app/appPaths.js";
import slackLogoSrc from "../../../assets/integrations/slackLogo.png";
import teamsLogoSrc from "../../../assets/integrations/teamsLogo.png";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { openOAuthSigninPopupForIntegration } from "../../../helpers/urlHelpers.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  deleteIntegration,
  selectAllIntegrations,
} from "../../integrations/integrationsSlice.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsSectionPage } from "../shared/settingsSection.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import {
  TeamsConnectionStatus,
  TeamsIntegrationTableStatus,
  useTeamsInstalledTeams,
} from "./teamsConnectionStatus.js";

// Registry of the sales-performance integrations rendered in the overview table
// and detail pages. Add an entry to surface a new integration. Every entry is an
// OAuth integration connected via openOAuthSigninPopupForIntegration.
type IntegrationConfig = {
  provider: IntegrationProvider;
  slug: string;
  name: string;
  description: string;
  logoSrc: string;
  disconnectWarning: string;
};

const integrationConfigs: IntegrationConfig[] = [
  {
    provider: IntegrationProvider.SLACK,
    slug: "slack",
    name: "Slack",
    logoSrc: slackLogoSrc,
    description:
      "Connect your Slack account so agents can send results to the right place. Agent results can be delivered to specific Slack channels or sent as DMs to specific teammates.",
    disconnectWarning:
      "Disconnecting Slack will stop agent results from being delivered to Slack.",
  },
  {
    provider: IntegrationProvider.TEAMS,
    slug: "microsoft-teams",
    name: "Microsoft Teams",
    logoSrc: teamsLogoSrc,
    description:
      "Connect your Microsoft 365 tenant so agents can send results to the right place. Agent results can be delivered to specific Teams channels or sent as DMs to specific teammates.",
    disconnectWarning:
      "Disconnecting Microsoft Teams will stop agent results from being delivered to Teams.",
  },
];

const renderIntegrationLogo = (integration: {
  name: string;
  logoSrc: string;
}) => (
  <span className="flex size-6 shrink-0 items-center justify-center rounded-sm border border-border-light bg-card">
    <img
      src={integration.logoSrc}
      alt=""
      aria-hidden="true"
      className="size-4 object-contain"
    />
  </span>
);

export const IntegrationSettingsSection: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { integrationSlug } = useParams<{ integrationSlug?: string }>();
  const allIntegrations = useAppSelector(selectAllIntegrations);
  const isTeamsEnabled = useIsFeatureFlagEnabled(FeatureFlagKey.MicrosoftTeams);
  const [disconnectTarget, setDisconnectTarget] =
    useState<IntegrationConfig | null>(null);

  const integrationsByProvider = new Map(
    allIntegrations.map((integration) => [integration.provider, integration]),
  );

  const getConnectedIntegration = (config: IntegrationConfig) =>
    integrationsByProvider.get(config.provider) ?? null;

  // Fetched once here and shared by the overview table's Teams status cell
  // and the Teams detail card, so navigating between them reuses the result.
  const teamsIntegration =
    integrationsByProvider.get(IntegrationProvider.TEAMS) ?? null;
  const { teamsWithInstalledAppFetch, retryFetch } = useTeamsInstalledTeams(
    teamsIntegration?.id ?? null,
  );

  const visibleIntegrationConfigs = integrationConfigs.filter(
    (config) => config.provider !== IntegrationProvider.TEAMS || isTeamsEnabled,
  );

  const selectedIntegration = visibleIntegrationConfigs.find(
    (config) => config.slug === integrationSlug,
  );

  const renderConnectedStatus = () => (
    <span className="text-xs font-medium text-success">Connected</span>
  );

  const renderIntegrationAction = (config: IntegrationConfig) => {
    if (getConnectedIntegration(config)) {
      return (
        <Can
          I={PermissionActions.DELETE}
          a={PermissionSubjects.INTEGRATIONS}
          passThrough={true}
        >
          {(allowed) => (
            <Button
              size={Size.SMALL}
              variant={ButtonVariant.Warning}
              isDisabled={!allowed}
              onClick={() => setDisconnectTarget(config)}
            >
              Disconnect
            </Button>
          )}
        </Can>
      );
    }

    return (
      <Can
        I={PermissionActions.CREATE}
        a={PermissionSubjects.INTEGRATIONS}
        passThrough={true}
      >
        {(allowed) => (
          <Button
            size={Size.SMALL}
            variant={ButtonVariant.Primary}
            isDisabled={!allowed}
            onClick={() => openOAuthSigninPopupForIntegration(config.provider)}
          >
            Connect
          </Button>
        )}
      </Can>
    );
  };

  const renderIntegrationTableStatus = (config: IntegrationConfig) => {
    if (!getConnectedIntegration(config)) {
      return renderIntegrationAction(config);
    }
    if (config.provider === IntegrationProvider.TEAMS) {
      return (
        <TeamsIntegrationTableStatus
          installedTeamsFetch={teamsWithInstalledAppFetch}
        />
      );
    }
    return renderConnectedStatus();
  };

  // Connected integrations first, then alphabetically by name within each group.
  const sortedIntegrations = [...visibleIntegrationConfigs].sort(
    (firstIntegration, secondIntegration) =>
      Number(Boolean(getConnectedIntegration(secondIntegration))) -
        Number(Boolean(getConnectedIntegration(firstIntegration))) ||
      firstIntegration.name.localeCompare(secondIntegration.name),
  );

  const renderOverview = () => (
    <div className="space-y-6">
      <SettingsTable>
        <SettingsTableHeaderSection>
          <tr>
            <SettingsTableHeaderCell className="w-full">
              Integration
            </SettingsTableHeaderCell>
            <SettingsTableHeaderCell className="w-32">
              Status
            </SettingsTableHeaderCell>
          </tr>
        </SettingsTableHeaderSection>
        <SettingsTableBodySection>
          {sortedIntegrations.map((integration) => (
            <tr
              key={integration.slug}
              className="cursor-pointer hover:bg-muted"
              role="link"
              tabIndex={0}
              onClick={() =>
                navigate(
                  appPaths.settingsIntegration.build({
                    integrationSlug: integration.slug,
                  }),
                )
              }
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  navigate(
                    appPaths.settingsIntegration.build({
                      integrationSlug: integration.slug,
                    }),
                  );
                }
              }}
            >
              <SettingsTableDataCell>
                <div className="flex items-center gap-2.5">
                  {renderIntegrationLogo(integration)}
                  <div className="font-semibold">{integration.name}</div>
                </div>
              </SettingsTableDataCell>
              <SettingsTableDataCell className="whitespace-nowrap">
                <div
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {renderIntegrationTableStatus(integration)}
                </div>
              </SettingsTableDataCell>
            </tr>
          ))}
        </SettingsTableBodySection>
      </SettingsTable>
    </div>
  );

  const renderDetail = (integration: IntegrationConfig) => {
    const connectedIntegration = getConnectedIntegration(integration);

    return (
      <div className="space-y-6">
        <RouterLink
          to={appPaths.settingsIntegrations.build()}
          className="inline-flex text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          Back to integrations
        </RouterLink>

        <div className="flex items-start gap-3">
          {renderIntegrationLogo(integration)}
          <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
            {integration.description}
          </p>
        </div>

        {/* Teams connects in two parts (consent + org-wide app install), so its
            detail page breaks the status down instead of a bare "Connected". */}
        {integration.provider === IntegrationProvider.TEAMS &&
        connectedIntegration ? (
          <TeamsConnectionStatus
            integrationId={connectedIntegration.id}
            installedTeamsFetch={teamsWithInstalledAppFetch}
            onRetryInstalledTeams={retryFetch}
          />
        ) : null}
      </div>
    );
  };

  return (
    <SettingsSectionPage
      title={
        selectedIntegration
          ? selectedIntegration.name
          : settingsSections.integrations.label
      }
      actions={
        selectedIntegration && renderIntegrationAction(selectedIntegration)
      }
    >
      {selectedIntegration
        ? renderDetail(selectedIntegration)
        : renderOverview()}

      <SimpleModal
        isOpen={disconnectTarget !== null}
        onCancel={() => setDisconnectTarget(null)}
        variant={SimpleModalVariant.Warning}
        title={disconnectTarget ? `Disconnect ${disconnectTarget.name}?` : ""}
        subtitle={disconnectTarget?.disconnectWarning}
        confirmButtonText="Disconnect"
        onConfirm={() => {
          if (disconnectTarget) {
            const connectedIntegration =
              getConnectedIntegration(disconnectTarget);
            if (connectedIntegration) {
              dispatch(deleteIntegration(connectedIntegration.id));
            }
          }

          setDisconnectTarget(null);
        }}
      />
    </SettingsSectionPage>
  );
};
